/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { ADD_TO_CART, GA4_EVENT_ADD_TO_CART, PAGE_DATA_LAYER } from '@/data/constants/gtm';
import { getGTMMultiAddToCartEventData } from '@/data/events/data/gtm/MultiAddToCart';
import { getGTMConfig } from '@/data/events/handlers/gtm';
import { GTMMultiAddToCartPayload } from '@/data/types/GTM';
import { error as logError } from '@/data/utils/loggerUtil';
import TagManager from 'react-gtm-module';

/**
 * Measure adding a product to a shopping cart by using an 'add' actionFieldObject and a list of
 *   productFieldObjects.
 * @param data transformed input payload from add-to-cart event (see data-type)
 */
export const measure_GA4 = async (
	data: Awaited<ReturnType<typeof getGTMMultiAddToCartEventData>>
) => {
	const dataLayerName = PAGE_DATA_LAYER;

	TagManager.dataLayer({ dataLayer: { event: ADD_TO_CART, ecommerce: null }, dataLayerName }); // Clean up previous ecommerce object

	TagManager.dataLayer({
		dataLayer: { event: GA4_EVENT_ADD_TO_CART, ...data },
		dataLayerName,
	});
};

/**
 * Measure adding a product to a shopping cart by using an 'add' actionFieldObject and a list of
 *   productFieldObjects.
 * @param data transformed input payload from add-to-cart event (see data-type)
 */
export const measure_UA = async (
	data: Awaited<ReturnType<typeof getGTMMultiAddToCartEventData>>
) => {
	const dataLayerName = PAGE_DATA_LAYER;
	TagManager.dataLayer({ dataLayer: { event: ADD_TO_CART, ecommerce: null }, dataLayerName }); // Clean up previous ecommerce object

	TagManager.dataLayer({ dataLayer: { event: ADD_TO_CART, ...data }, dataLayerName });
};

export const sendGTMMultiAddToCartEvent = async (payload: GTMMultiAddToCartPayload) => {
	const { settings } = payload;
	const { ga4, ua } = getGTMConfig(settings);
	if (ua || ga4) {
		const data = await getGTMMultiAddToCartEventData(payload);

		if (ua) {
			try {
				await measure_UA(data);
			} catch (error) {
				logError(
					undefined,
					'MultiAddToCart: sendGTMMultiAddToCartEvent: measure_UA: error: %o',
					error
				);
			}
		}

		if (ga4) {
			try {
				await measure_GA4(data);
			} catch (error) {
				logError(
					undefined,
					'MultiAddToCart: sendGTMMultiAddToCartEvent: measure_GA4: error: %o',
					error
				);
			}
		}
	}
};
